import { createRouter, createWebHistory, RouterView } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { confirm } from "@/utils/confirm"; // ✅ Add this
import Home from "@/views/Home.vue";
import Client from "@/views/Client.vue";
import Callback from "@/views/Callback.vue"; // now a real component
import CustomerSetting from "@/views/CustomerSetting.vue";
import AuditGrid from "@/views/AuditGrid.vue";
import UserManager from "@/views/UserManager.vue";
import { customerForUser, selectCustomer, clearCustomer } from '@/utils/customerSelection';
import { resolveAdminStart } from '@/utils/adminStart';
import api from '@/api/client';

const routes = [
  {
    path: "/signup",
    name: "TrialSignup",
    component: () => import('@/views/TrialSignup.vue'),
    meta: { breadcrumb: "Start free trial", publicPage: true },
  },
  { path: '/start', name: 'AdminStart', component: { render: () => null }, meta: { requiresAuth: true } },
  {
    path: "/",
    name: "Customers",
    component: () => import('@/views/Customers.vue'),
    meta: { breadcrumb: "Customers", requiresAuth: true },
  },
  {
    path: "/customers/:customerId",
    name: "CustomerDetails",
    component: () => import('@/views/CustomerDetails.vue'),
    props: true,
    meta: { breadcrumb: "Customer details", requiresAuth: true },
  },
  {
    path: "/subscriptions",
    name: "Subscriptions",
    component: () => import('@/views/Subscriptions.vue'),
    meta: { breadcrumb: "Subscriptions", requiresAuth: true },
  },
  {
    path: "/sites",
    name: "Sites",
    component: Home,
    meta: { breadcrumb: "Sites", requiresAuth: true, formName: "Home" },
  },
  {
    path: "/:id/Tours",
    name: "Tours",
    redirect: { name: "AdminStart" },
    meta: { requiresAuth: true },
  },
  {
    path: "/sites/:siteId/overlays",
    name: "SiteOverlays",
    component: () => import('@/views/SiteOverlays.vue'),
    meta: { breadcrumb: "Maps & overlays", requiresAuth: true },
    props: true,
  },
  {
    path: "/sites/:siteId/tours/:tourId/edit",
    name: "TourEditor",
    component: () => import("@/views/TourEditor.vue"),
    meta: { breadcrumb: "Edit Tour", requiresAuth: true, formName: "TourEditor" },
    props: true,
  },
  {
    path: "/customer-settings",
    name: "CustomerSettings",
    component: CustomerSetting,
    meta: { breadcrumb: "Customer Settings", requiresAuth: true, formName: "CustomerSetting" },
  },
  {
    path: "/client",
    name: "Client",
    component: Client,
    meta: { breadcrumb: "Client", requiresAuth: true, formName: "Client" },
  },
  {
    path: "/users",
    name: "Users",
    component: UserManager,
    meta: { breadcrumb: "Users", requiresAuth: true, formName: "UserManager" },
  },
  {
    path: "/tour-progress",
    name: "TourProgress",
    component: () => import("@/views/TourProgress.vue"),
    meta: { breadcrumb: "Visitor Dashboard", requiresAuth: true, formName: "TourProgress" },
  },
  {
    path: "/visitor-report",
    name: "VisitorReport",
    component: () => import("@/views/VisitorReport.vue"),
    meta: { breadcrumb: "Visitor Report", requiresAuth: true, formName: "VisitorReport" },
  },
  {
    path: "/audit",
    name: "AuditTrail",
    component: AuditGrid,
    meta: { breadcrumb: "Audit Trail", requiresAuth: true, formName: "AuditGrid" },
  },
  { path: "/callback", name: "Callback", component: Callback },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

let startResolved = false;
router.beforeEach(async (to, from) => {
  const auth = useAuthStore();

  // Allow OIDC callback route to pass without checks
  if (to.name === "Callback") {
    return true;
  }

  await auth.init();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    auth.login(to.fullPath);
    return false;
  }

  if (to.name === 'AdminStart' || (!startResolved && to.name === 'Customers' && from.matched.length === 0)) {
    startResolved = true;
    return { ...await resolveAdminStart(api, auth.user?.profile?.sub, selectCustomer, clearCustomer), replace: true };
  }

  if (to.name === 'Customers' || to.name === 'CustomerDetails' || to.name === 'Subscriptions') {
    try {
      const { data: access } = await api.get('/customers/access');
      if (!access.platformAdministrator && access.ownCustomerId) {
        const ownId = String(access.ownCustomerId);
        if (to.name === 'Customers' || to.name === 'Subscriptions' || String(to.params.customerId) !== ownId)
          return { name: 'CustomerDetails', params: { customerId: ownId }, replace: true };
      }
    } catch { /* The destination view reports access errors. */ }
  }

  const unscoped = ['Customers', 'CustomerDetails', 'Subscriptions', 'VisitorReport', 'Callback'];
  if (to.meta.requiresAuth && !unscoped.includes(to.name) && !customerForUser(auth.user?.profile?.sub))
    return { name: 'Customers' };

  return true;
});

export default router;
